import { useCallback, useState } from 'react'

import {
  fetchAiTagProgress,
  useStartAiAnalysisMutation,
  useStartDiffAnalysisMutation
} from '@/common/api/modules/aiTag'
import { AITagCategory, AnalysisProgress } from '@/common/model/recommend/aiTag'

import { BadgeType } from '@/components/view/atoms/badge/Badge'
import { TextColor } from '@/components/view/atoms/text/Text'

// -----------------------------------------------
// Category constants
// -----------------------------------------------

export const AI_TAG_CATEGORY_BADGE_MAP: Record<AITagCategory, BadgeType> = {
  [AITagCategory.PAIN_POINT]: BadgeType.CRITICAL,
  [AITagCategory.EMOTIONAL_GAIN]: BadgeType.CAUTION,
  [AITagCategory.TARGET_PERSONA]: BadgeType.CAUTION,
  [AITagCategory.BUYING_MOTIVE]: BadgeType.EMPHASIS,
  [AITagCategory.USAGE_SCENE]: BadgeType.SUCCESS,
  [AITagCategory.PURCHASE_INTENT]: BadgeType.SQUARE_PRIMARY,
  [AITagCategory.FEATURE]: BadgeType.SUCCESS,
  [AITagCategory.PRODUCT_STRENGTH]: BadgeType.CAUTION,
  [AITagCategory.MATERIAL]: BadgeType.SECONDARY,
  [AITagCategory.SEASON]: BadgeType.EMPHASIS,
  [AITagCategory.OCCASION]: BadgeType.EMPHASIS
}

export const aiTagCategoryLabel = (key: AITagCategory): string => {
  switch (key) {
    case AITagCategory.PAIN_POINT:
      return '悩み・課題'
    case AITagCategory.EMOTIONAL_GAIN:
      return '感情的ベネフィット'
    case AITagCategory.TARGET_PERSONA:
      return 'ペルソナ'
    case AITagCategory.BUYING_MOTIVE:
      return '購買動機'
    case AITagCategory.USAGE_SCENE:
      return '利用シーン'
    case AITagCategory.PURCHASE_INTENT:
      return '購入パターン'
    case AITagCategory.FEATURE:
      return '商品特徴'
    case AITagCategory.PRODUCT_STRENGTH:
      return '競争優位'
    case AITagCategory.MATERIAL:
      return '素材'
    case AITagCategory.SEASON:
      return '季節'
    case AITagCategory.OCCASION:
      return 'イベント'
    default:
      return key
  }
}

export const extractImportanceScoreRank = (
  importanceScore: number
): { name: string; color: TextColor } => {
  const percent = Math.round(importanceScore * 100)

  if (percent >= 80) return { name: '高', color: TextColor.CRITICAL }
  if (percent >= 50) return { name: '中', color: TextColor.SUCCESS }
  return { name: '低', color: TextColor.CAUTION }
}

// -----------------------------------------------
// useAiTagAnalysis: analyze + polling hook
// -----------------------------------------------

interface UseAiTagAnalysisOptions {
  siteId: number
  onComplete: () => void
}

export const useAiTagAnalysis = ({ siteId, onComplete }: UseAiTagAnalysisOptions) => {
  const [analyzing, setAnalyzing] = useState(false)
  const [diffAnalyzing, setDiffAnalyzing] = useState(false)
  const [progress, setProgress] = useState<AnalysisProgress | null>(null)
  const [analyzeMsg, setAnalyzeMsg] = useState('')

  const { trigger: startAnalysis } = useStartAiAnalysisMutation()
  const { trigger: startDiffAnalysis } = useStartDiffAnalysisMutation()

  const pollProgress = useCallback(() => {
    const interval = setInterval(async () => {
      try {
        const pData = await fetchAiTagProgress(siteId)
        setProgress(pData)
        if (pData.status === 'completed' || pData.status === 'error') {
          clearInterval(interval)
          setAnalyzing(false)
          setDiffAnalyzing(false)
          setAnalyzeMsg(pData.message)
          if (pData.status === 'completed') {
            setTimeout(() => {
              onComplete()
              setProgress(null)
            }, 3000)
          }
        }
      } catch {}
    }, 2000)
    return interval
  }, [siteId, onComplete])

  const analyze = useCallback(async () => {
    setAnalyzing(true)
    setAnalyzeMsg('')
    setProgress(null)
    try {
      await startAnalysis({ site_id: siteId })
      pollProgress()
    } catch (e: any) {
      setAnalyzeMsg(`エラー: ${e?.message || '解析開始失敗'}`)
      setAnalyzing(false)
    }
  }, [siteId, startAnalysis, pollProgress])

  const diffAnalyze = useCallback(async () => {
    setDiffAnalyzing(true)
    setAnalyzeMsg('')
    try {
      const data = await startDiffAnalysis({ site_id: siteId })
      setAnalyzeMsg(data.message)
      if (data.status === 'started') {
        setTimeout(() => {
          onComplete()
          setAnalyzeMsg('')
        }, 5000)
      }
    } catch (e: any) {
      setAnalyzeMsg(`エラー: ${e?.message || '差分解析開始失敗'}`)
    } finally {
      setDiffAnalyzing(false)
    }
  }, [siteId, startDiffAnalysis, onComplete])

  return {
    analyzing,
    diffAnalyzing,
    isProcessing: analyzing || diffAnalyzing,
    progress,
    analyzeMsg,
    analyze,
    diffAnalyze
  }
}
